/**
 * TaxCalendar Component
 * 
 * Displays upcoming tax deadlines with countdown and urgency indicators.
 * 
 * Features:
 * - List of upcoming tax events
 * - Days remaining countdown
 * - Color-coded urgency (red < 30, yellow < 60, blue > 60)
 * - Event type badges (Payment, Filing)
 * - Due date display
 * - Icons for visual hierarchy
 * - Responsive design
 * 
 * Created: 2025-01-16 (Dashboard Redesign Phase 2)
 * Phase: Dashboard Redesign - Phase 2: Content Cards
 */

import React from 'react';

const TaxCalendar = ({ deadlines }) => {
  const calculateDaysRemaining = (dueDate) => {
    const today = new Date();
    const deadline = new Date(dueDate);
    const diffTime = deadline - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getUrgencyColor = (daysRemaining) => {
    if (daysRemaining < 0) return 'red'; // Overdue
    if (daysRemaining <= 30) return 'red'; // Urgent
    if (daysRemaining <= 60) return 'yellow'; // Warning
    return 'blue'; // Normal
  };

  const getUrgencyStyles = (color) => {
    const styles = {
      red: {
        badge: 'bg-red-100 text-red-700 border-red-200',
        icon: 'text-red-600',
        bar: 'bg-red-100',
      },
      yellow: {
        badge: 'bg-yellow-100 text-yellow-700 border-yellow-200',
        icon: 'text-yellow-600',
        bar: 'bg-yellow-100',
      },
      blue: {
        badge: 'bg-blue-100 text-blue-700 border-blue-200',
        icon: 'text-blue-600',
        bar: 'bg-blue-100',
      },
    };
    return styles[color] || styles.blue;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const getEventTypeBadge = (type) => {
    const badges = {
      payment: 'bg-purple-100 text-purple-700',
      filing: 'bg-green-100 text-green-700',
    };
    return badges[type] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
      {/* Header */}
      <div className="mb-4">
        <div className="flex items-center space-x-2 mb-2">
          <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <h3 className="text-lg font-bold text-gray-900">Tax Calendar</h3>
        </div>
        <p className="text-sm text-gray-600">
          Upcoming deadlines and important dates
        </p>
      </div>

      {/* Deadline List */}
      <div className="space-y-4">
        {(deadlines || []).map((deadline, index) => {
          const daysRemaining = calculateDaysRemaining(deadline.dueDate);
          const urgencyColor = getUrgencyColor(daysRemaining);
          const styles = getUrgencyStyles(urgencyColor);

          return (
            <div 
              key={deadline.id || index}
              className={`p-4 rounded-lg border ${styles.bar} ${styles.badge.replace('100', '50')}`}
            >
              <div className="flex items-start justify-between">
                {/* Left: Event Info */}
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <h4 className="text-sm font-bold text-gray-900">
                      {deadline.title}
                    </h4>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getEventTypeBadge(deadline.type)}`}>
                      {deadline.type.charAt(0).toUpperCase() + deadline.type.slice(1)}
                    </span>
                  </div>
                  <div className="flex items-center space-x-4 text-xs text-gray-600">
                    <div className="flex items-center space-x-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span>Due: {formatDate(deadline.dueDate)}</span>
                    </div>
                  </div>
                </div>

                {/* Right: Countdown */}
                <div className={`text-right ml-4 flex flex-col items-end`}>
                  <div className={`flex items-center space-x-1 px-3 py-1 rounded-full border ${styles.badge}`}>
                    <svg className={`w-4 h-4 ${styles.icon}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-sm font-bold">
                      {daysRemaining < 0 ? 'Overdue' : `${daysRemaining} days`}
                    </span>
                  </div>
                  {daysRemaining <= 30 && daysRemaining > 0 && (
                    <span className="text-xs text-red-600 font-medium mt-1">
                      Urgent!
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* View All Link */}
      <div className="mt-6 pt-4 border-t border-gray-200 text-center">
        <button className="text-sm text-blue-600 hover:text-blue-700 font-medium hover:underline">
          View Full Tax Calendar →
        </button>
      </div>
    </div>
  );
};

// Default props
TaxCalendar.defaultProps = {
  deadlines: [
    {
      id: 1,
      title: 'Q1 Estimated Taxes',
      dueDate: '2025-01-15',
      type: 'payment',
    },
    {
      id: 2,
      title: 'Annual Tax Return Filing',
      dueDate: '2025-04-15',
      type: 'filing',
    },
    {
      id: 3,
      title: 'Q2 Estimated Taxes',
      dueDate: '2025-06-17',
      type: 'payment',
    },
  ],
};

export default TaxCalendar;
